// Mutuelle santé et soins : la formule choisie décide de ce qui reste à payer.
// Les restes à charge suivent les règles de 2026 : la Sécurité sociale rembourse sa part (70 % d'une
// consultation, 60 % des soins dentaires et de la kiné…) ; sans mutuelle, le reste est pour soi ; la formule
// de base paie ce reste, mais peu les dépassements d'honoraires, l'optique et le dentaire au-delà des tarifs
// de la Sécurité sociale ; la renforcée couvre presque tout. Quelques euros de franchises restent toujours.
import type { GameState, StageId } from '../../engine/state';
import { careCost, mutuellePrice } from '../../game/health';
import type { EventDef } from '../events';
import { age, between, income, roll, simpleEvent, working } from './kit';

const ALL_STAGES: StageId[] = ['prologue', 'stage1', 'stage2'];
const ADULT: StageId[] = ['stage1', 'stage2'];

/** Des lunettes sur le nez : l'opticien est déjà passé par là (voir lunettes_opticien). */
function wearsGlasses(state: GameState): boolean {
  return (state.eventCounts.lunettes_opticien ?? 0) > 0;
}

/** Une mutuelle à soi : ni celle des parents, ni la complémentaire santé solidaire. */
function ownMutuelle(state: GameState): boolean {
  return !state.flags.mutuelle_parents && !state.flags.css;
}

/** Sans médecin traitant déclaré, une consultation est remboursée à 30 % au lieu de 70 % : environ 12 € de plus. */
function outsidePathway(state: GameState): number {
  const outside = state.flags.sans_medecin_traitant === true && !state.flags.medecin_traitant;
  return outside ? Math.round(12 * state.priceLevel) : 0;
}

export const EVENTS: EventDef[] = [
  // Choisir sa formule
  simpleEvent({
    id: 'mutuelle_etudiante_rentree',
    kind: 'quotidien',
    stages: ['prologue'],
    season: [9],
    cooldownDays: 300,
    maxCount: 3,
    condition: (state) => state.path === 'studies' && ownMutuelle(state),
    params: (state) => ({ base: mutuellePrice(state, 'base'), renforcee: mutuellePrice(state, 'renforcee') }),
    page: 'budget',
    choices: [
      { id: 'garder' },
      { id: 'base', visible: (state) => state.mutuelleLevel !== 'base', effects: { mutuelle: { level: 'base' } } },
      {
        id: 'renforcee',
        visible: (state) => state.mutuelleLevel !== 'renforcee',
        effects: { mutuelle: { level: 'renforcee' } },
      },
      { id: 'aucune', visible: (state) => state.mutuelleLevel !== 'aucune', effects: { mutuelle: { level: 'aucune' } } },
    ],
  }),
  simpleEvent({
    id: 'mutuelle_parents',
    kind: 'proches',
    stages: ['prologue'],
    maxCount: 1,
    condition: (state) => state.milieu !== 'modeste' && ownMutuelle(state),
    choices: [
      {
        id: 'accepter',
        // La mutuelle familiale des parents : plus complète dans un milieu aisé, et ce sont eux qui paient.
        effects: (state) => ({
          mutuelle: { level: state.milieu === 'aise' ? 'renforcee' : 'base', monthly: 0 },
          energy: -1,
          wellbeing: -1,
          flag: 'mutuelle_parents',
        }),
      },
      { id: 'refuser', effects: { wellbeing: 1 } },
    ],
  }),
  simpleEvent({
    id: 'mutuelle_option_renforcee',
    kind: 'travail',
    stages: ADULT,
    maxCount: 2,
    condition: (state) => working(state) && state.mutuelleLevel === 'base',
    params: (state) => ({ extra: mutuellePrice(state, 'renforcee') - mutuellePrice(state, 'base') }),
    choices: [
      { id: 'prendre', effects: { mutuelle: { level: 'renforcee' } } },
      { id: 'reflechir', effects: { energy: -1, followUp: { event: 'mutuelle_tableau_garanties', days: 7 } } },
      { id: 'garder' },
    ],
  }),
  simpleEvent({
    // Une semaine après avoir demandé le tableau des garanties de l'option.
    id: 'mutuelle_tableau_garanties',
    kind: 'travail',
    stages: ADULT,
    manual: true,
    // Deux exemples : la monture qui plaît (voir lunettes_opticien), un spécialiste avec dépassement d'honoraires.
    params: (state) => ({
      lunettesBase: Math.round(200 * state.priceLevel),
      lunettesOption: Math.round(30 * state.priceLevel),
      specialisteBase: Math.round(52 * state.priceLevel),
      specialisteOption: Math.round(5 * state.priceLevel),
    }),
    choices: [{ id: 'prendre', effects: { mutuelle: { level: 'renforcee' } } }, { id: 'garder' }],
  }),
  simpleEvent({
    id: 'cotisation_mutuelle_hausse',
    kind: 'imprevu',
    stages: ALL_STAGES,
    season: [1],
    cooldownDays: 300,
    // Une mutuelle payée de sa poche : la mutuelle étudiante, ou l'option renforcée au travail.
    condition: (state) =>
      (state.expenses.mutuelle?.amount ?? 0) > 0 && (!working(state) || state.mutuelleLevel === 'renforcee'),
    params: (state) => {
      // Une hausse de 5 à 8 %, au moins 2 € par mois.
      const current = state.expenses.mutuelle?.amount ?? 30;
      return { hausse: Math.max(2, Math.round((current * roll(state, 5, 8)) / 100)) };
    },
    page: 'inflation',
    choices: [
      { id: 'accepter', effects: (_, p) => ({ addExpense: { id: 'mutuelle', amount: p.hausse } }) },
      { id: 'comparer', visible: (state) => !working(state), effects: { energy: -3, knowledge: 1 } },
      {
        id: 'baisser',
        visible: (state) => state.mutuelleLevel === 'renforcee',
        effects: { mutuelle: { level: 'base' } },
      },
      { id: 'resilier', visible: (state) => !working(state), effects: { mutuelle: { level: 'aucune' } } },
    ],
  }),
  simpleEvent({
    id: 'complementaire_sante_solidaire',
    kind: 'chance',
    stages: ['prologue'],
    maxCount: 1,
    // Sous le plafond de ressources (environ 850 € par mois pour une personne seule), elle est gratuite.
    condition: (state) => state.milieu === 'modeste' && income(state) < 850 && ownMutuelle(state),
    choices: [
      { id: 'demander', effects: { energy: -3, mutuelle: { level: 'base', monthly: 0 }, flag: 'css' } },
      { id: 'laisser' },
    ],
  }),

  // Prévention et bonnes surprises
  simpleEvent({
    id: 'bilan_prevention',
    kind: 'quotidien',
    stages: ALL_STAGES,
    maxCount: 1,
    condition: (state) => age(state) <= 25,
    choices: [{ id: 'pharmacien', effects: { energy: -1, wellbeing: 2 } }, { id: 'ignorer' }],
  }),
  simpleEvent({
    id: 'medecin_traitant',
    kind: 'quotidien',
    stages: ALL_STAGES,
    maxCount: 2,
    condition: (state) => state.housing !== 'parents' && !state.flags.medecin_traitant,
    // Une consultation à 30 € : 70 % remboursés avec un médecin traitant, 30 % sans, moins 2 € de participation.
    params: (state) => {
      const consult = Math.round(30 * state.priceLevel);
      return { consult, normal: Math.round(consult * 0.7) - 2, reduced: Math.round(consult * 0.3) - 2 };
    },
    choices: [
      { id: 'declarer', effects: { energy: -1, flag: 'medecin_traitant' } },
      { id: 'plus_tard', effects: { flag: 'sans_medecin_traitant' } },
    ],
  }),
  simpleEvent({
    id: 'carte_vitale_perdue',
    kind: 'quotidien',
    stages: ALL_STAGES,
    maxCount: 1,
    params: (state) => {
      // Sans tiers payant, on avance toute l'ordonnance ; le remboursement rend tout, sauf le reste à charge.
      const cost = between(state, 170, 220);
      return { cost, refund: Math.max(0, cost - careCost(state, { aucune: 20, base: 3, renforcee: 3 })) };
    },
    choices: [
      {
        id: 'avancer',
        effects: (_, p) => ({
          cash: -p.cost,
          energy: -1,
          followUp: { event: 'feuille_de_soins_remboursee', days: 25 },
        }),
      },
      { id: 'chercher', effects: { energy: -2, wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    // Trois semaines et demie après l'envoi de la feuille de soins papier.
    id: 'feuille_de_soins_remboursee',
    kind: 'chance',
    stages: ALL_STAGES,
    manual: true,
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.refund, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.refund }) },
    ],
  }),
  simpleEvent({
    // L'examen dentaire gratuit chaque année jusqu'à 24 ans ; ses soins aussi, s'ils commencent dans les trois mois.
    id: 'examen_dentaire_gratuit',
    kind: 'quotidien',
    stages: ALL_STAGES,
    maxCount: 2,
    condition: (state) => age(state) <= 24,
    choices: [
      { id: 'soigner', effects: { energy: -2 } },
      { id: 'repousser', effects: { followUp: { event: 'rage_de_dents', days: 120 } } },
    ],
  }),
  simpleEvent({
    // Quatre mois après avoir repoussé les soins de la carie : ils ne sont plus pris en charge à 100 %.
    id: 'rage_de_dents',
    kind: 'imprevu',
    stages: ALL_STAGES,
    manual: true,
    bad: true,
    shock: 7,
    params: (state) => ({
      garde: careCost(state, { aucune: 55, base: 3, renforcee: 3 }),
      lundi: careCost(state, { aucune: 40, base: 3, renforcee: 3 }),
      extraction: careCost(state, { aucune: 25, base: 2, renforcee: 2 }),
    }),
    choices: [
      { id: 'garde', effects: (_, p) => ({ cash: -p.garde, energy: -2 }) },
      { id: 'lundi', effects: (_, p) => ({ cash: -p.lundi, energy: -4, wellbeing: -2 }) },
      { id: 'arracher', effects: (_, p) => ({ cash: -p.extraction, energy: -4, wellbeing: -4 }) },
    ],
  }),

  // Les tentations : ce que ni la Sécurité sociale ni (presque) aucune mutuelle ne rembourse
  simpleEvent({
    id: 'blanchiment_dents',
    kind: 'tentation',
    pleasureCategory: 'soins',
    stages: ALL_STAGES,
    maxCount: 1,
    params: (state) => ({ price: between(state, 700, 1100), kit: between(state, 150, 180) }),
    choices: [
      { id: 'cabinet', effects: (_, p) => ({ cash: -p.price, pleasure: 4 }) },
      { id: 'kit', effects: (_, p) => ({ cash: -p.kit, pleasure: 1 }) },
      { id: 'rien' },
    ],
  }),
  simpleEvent({
    id: 'lentilles_contact',
    kind: 'tentation',
    pleasureCategory: 'soins',
    stages: ALL_STAGES,
    maxCount: 1,
    condition: wearsGlasses,
    // Six mois de lentilles : la Sécurité sociale ne paie rien, les mutuelles un forfait par an.
    params: (state) => ({
      journalieres: careCost(state, { aucune: 220, base: 160, renforcee: 70 }),
      mensuelles: careCost(state, { aucune: 120, base: 60, renforcee: 15 }),
    }),
    choices: [
      { id: 'journalieres', effects: (_, p) => ({ cash: -p.journalieres, pleasure: 4 }) },
      { id: 'mensuelles', effects: (_, p) => ({ cash: -p.mensuelles, energy: -1, pleasure: 2 }) },
      { id: 'lunettes' },
    ],
  }),
  simpleEvent({
    id: 'cure_vitamines',
    kind: 'tentation',
    pleasureCategory: 'soins',
    stages: ALL_STAGES,
    maxCount: 1,
    params: (state) => ({ price: between(state, 160, 190), cheap: between(state, 110, 118) }),
    choices: [
      { id: 'cure', effects: (_, p) => ({ cash: -p.price, pleasure: 3 }) },
      { id: 'pharmacie', effects: (_, p) => ({ cash: -p.cheap, pleasure: 1 }) },
      { id: 'rien' },
    ],
  }),

  // Les soins imprévus : la dépense est là, la formule décide de sa taille
  simpleEvent({
    id: 'cheville_tordue',
    kind: 'imprevu',
    stages: ALL_STAGES,
    bad: true,
    shock: 6,
    maxCount: 2,
    // Le forfait des urgences, l'attelle et dix séances de kiné ; sans la kiné, les urgences et l'attelle.
    params: (state) => ({
      cost: careCost(state, { aucune: 125, base: 20, renforcee: 10 }),
      low: careCost(state, { aucune: 45, base: 8, renforcee: 3 }),
    }),
    page: 'fonds_urgence',
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost, energy: -2 }) },
      { id: 'carte', effects: (_, p) => ({ cash: -p.cost, energy: -2 }) },
      { id: 'sans_kine', effects: (_, p) => ({ cash: -p.low, energy: -4, wellbeing: -2, fatigueDays: 20 }) },
    ],
  }),
  simpleEvent({
    id: 'appendicite',
    kind: 'imprevu',
    stages: ALL_STAGES,
    bad: true,
    shock: 8,
    maxCount: 1,
    // Trois jours d'hôpital : le forfait journalier et la participation de 24 € sur l'opération, que toute
    // mutuelle prend en charge ; la chambre particulière, seulement la renforcée.
    params: (state) => ({
      forfait: Math.round(20 * state.priceLevel),
      double: careCost(state, { aucune: 105, base: 4, renforcee: 4 }),
      single: careCost(state, { aucune: 225, base: 125, renforcee: 4 }),
    }),
    page: 'fonds_urgence',
    choices: [
      { id: 'double', effects: (_, p) => ({ cash: -p.double, energy: -3, wellbeing: -1, restDays: 14 }) },
      { id: 'particuliere', effects: (_, p) => ({ cash: -p.single, energy: -3, wellbeing: 1, restDays: 14 }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.single, energy: -3, wellbeing: 1, restDays: 14 }) },
    ],
  }),
  simpleEvent({
    id: 'couteau_urgences',
    kind: 'imprevu',
    stages: ALL_STAGES,
    bad: true,
    shock: 5,
    maxCount: 2,
    // Le forfait patient urgences (environ 20 €, remboursé par les mutuelles), puis l'infirmière et la pharmacie.
    params: (state) => ({
      fpu: Math.round(20 * state.priceLevel),
      cost: careCost(state, { aucune: 35, base: 3, renforcee: 3 }),
      strips: between(state, 116, 130),
    }),
    choices: [
      { id: 'urgences', effects: (_, p) => ({ cash: -p.cost, energy: -4 }) },
      { id: 'strips', effects: (_, p) => ({ cash: -p.strips, energy: -1, wellbeing: -2 }) },
    ],
  }),
  simpleEvent({
    id: 'menisque_genou',
    kind: 'imprevu',
    stages: ADULT,
    bad: true,
    shock: 7,
    maxCount: 1,
    // En clinique, les dépassements du chirurgien et de l'anesthésiste ; à l'hôpital public, aucun.
    params: (state) => ({
      clinic: careCost(state, { aucune: 750, base: 520, renforcee: 60 }),
      hospital: careCost(state, { aucune: 45, base: 4, renforcee: 4 }),
    }),
    page: 'fonds_urgence',
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: -p.clinic, energy: -3, restDays: 14 }) },
      { id: 'carte', effects: (_, p) => ({ cash: -p.clinic, energy: -3, restDays: 14 }) },
      {
        id: 'credit',
        // Avec la renforcée, il ne reste presque rien à payer : pas de quoi emprunter.
        visible: (state) => state.mutuelleLevel !== 'renforcee',
        effects: (_, p) => ({
          loan: { amount: p.clinic, rate: 0.09, months: 10 },
          energy: -3,
          restDays: 14,
          page: 'cout_credit',
        }),
      },
      {
        id: 'hopital',
        effects: (_, p) => ({ cash: -p.hospital, energy: -5, wellbeing: -3, fatigueDays: 90 }),
      },
    ],
  }),
  simpleEvent({
    id: 'lunettes_cassees',
    kind: 'imprevu',
    stages: ALL_STAGES,
    bad: true,
    shock: 5,
    maxCount: 1,
    condition: wearsGlasses,
    // Moins de deux ans après l'achat, une nouvelle paire n'est pas remboursée ; seule la renforcée couvre la casse.
    params: (state) => ({
      cost: careCost(state, { aucune: 210, base: 190, renforcee: 40 }),
      online: between(state, 190, 250),
    }),
    page: 'fonds_urgence',
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'carte', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'ligne', effects: (_, p) => ({ cash: -p.online, energy: -2, wellbeing: -1 }) },
    ],
  }),

  // Les soins qu'on peut choisir : consulter, attendre, se débrouiller
  simpleEvent({
    id: 'fievre_dimanche',
    kind: 'imprevu',
    stages: ALL_STAGES,
    season: [12, 1, 2],
    cooldownDays: 300,
    maxCount: 3,
    // Une consultation du dimanche, majorée mais remboursée ; à la pharmacie, sans ordonnance, rien ne l'est.
    params: (state) => ({
      garde: careCost(state, { aucune: 28, base: 5, renforcee: 5 }),
      pharma: between(state, 124, 140),
    }),
    choices: [
      { id: 'garde', effects: (_, p) => ({ cash: -p.garde, energy: -2 }) },
      { id: 'pharmacie', effects: (_, p) => ({ cash: -p.pharma, energy: -3, wellbeing: -1 }) },
      { id: 'attendre', effects: { energy: -4, wellbeing: -2, fatigueDays: 7 } },
    ],
  }),
  simpleEvent({
    id: 'grain_de_beaute',
    kind: 'quotidien',
    stages: ALL_STAGES,
    maxCount: 1,
    // Une dermatologue qui dépasse ses honoraires : la formule de base ne paie pas le dépassement, la renforcée si.
    params: (state) => ({
      now: careCost(state, { aucune: 61, base: 52, renforcee: 5 }) + outsidePathway(state),
      photo: careCost(state, { aucune: 11, base: 2, renforcee: 2 }) + outsidePathway(state),
    }),
    choices: [
      { id: 'consulter', effects: (_, p) => ({ cash: -p.now, wellbeing: 2 }) },
      { id: 'photo', effects: (_, p) => ({ cash: -p.photo, energy: -1, wellbeing: 1 }) },
      { id: 'surveiller', effects: { wellbeing: -2 } },
    ],
  }),
  simpleEvent({
    id: 'soutien_psy',
    kind: 'quotidien',
    stages: ALL_STAGES,
    maxCount: 1,
    condition: (state) => state.wellbeing < 50,
    // Huit séances à 50 € : 60 % par la Sécurité sociale, le reste par la mutuelle s'il y en a une.
    params: (state) => ({ cost: careCost(state, { aucune: 160, base: 0, renforcee: 0 }) }),
    choices: [
      { id: 'seances', effects: (_, p) => ({ cash: -p.cost, energy: -2, wellbeing: 5 }) },
      { id: 'proches', effects: { energy: -1, wellbeing: 2 } },
      { id: 'attendre', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'vaccins_voyage',
    kind: 'quotidien',
    stages: ALL_STAGES,
    maxCount: 1,
    // Des vaccins de voyage jamais remboursés par la Sécurité sociale ; la renforcée a un forfait pour eux.
    params: (state) => ({
      all: careCost(state, { aucune: 165, base: 145, renforcee: 65 }),
      minimum: careCost(state, { aucune: 95, base: 85, renforcee: 15 }),
    }),
    choices: [
      { id: 'tous', effects: (_, p) => ({ cash: -p.all, energy: -1, wellbeing: 1 }) },
      { id: 'obligatoire', effects: (_, p) => ({ cash: -p.minimum, energy: -1 }) },
      { id: 'renoncer', effects: { wellbeing: -2 } },
    ],
  }),
];
